import { useFragment } from "@/generated";
import {
  DuplicateSlidesDocument,
  type SlideFieldsFragment,
  SlideFieldsFragmentDoc,
} from "@/generated/graphql";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { updateDuplicateSlideCache } from "./useDuplicateSlide";

export function useDuplicateSlides() {
  const navigate = useNavigate();
  const [duplicateSlidesMutation, { loading }] = useMutation(DuplicateSlidesDocument, {
    update(cache, { data }, { variables }) {
      if (!data?.duplicateSlides) return;
      const duplicationType = variables?.duplicationType;
      try {
        for (const maskedSlide of data.duplicateSlides) {
          const slide = useFragment(SlideFieldsFragmentDoc, maskedSlide) as SlideFieldsFragment;
          updateDuplicateSlideCache(cache, slide, duplicationType);
        }
      } catch (error) {
        console.error("Cache update failed:", error);
      }
    },
  });

  const duplicateSlides = async (
    slideIds: string[],
    targetLessonId: string,
    duplicationType: "unique" | "linked",
    insertAfterSortOrder?: number | null
  ) => {
    return withToast(
      async () => {
        const result = await duplicateSlidesMutation({
          variables: { slideIds, targetLessonId, duplicationType, insertAfterSortOrder },
        });

        const duplicated = result.data?.duplicateSlides;
        if (duplicated?.length) {
          const firstSlide = duplicated[0]
            ? useFragment(SlideFieldsFragmentDoc, duplicated[0])
            : undefined;
          if (firstSlide) {
            saveScrollTarget(`slide-${firstSlide.id}`, targetLessonId);
          }
          navigate({
            to: "/admin/lesson/$lessonId",
            params: { lessonId: targetLessonId },
          });
        }

        return duplicated;
      },
      {
        loading: "Duplicating slides...",
        success: `Successfully duplicated ${slideIds.length} slide${slideIds.length === 1 ? "" : "s"}`,
        error: "Failed to duplicate slides",
      },
      "duplicateSlides"
    );
  };

  return { duplicateSlides, loading };
}
